﻿import React, { useState, useEffect } from 'react';
import { HplcBenchSheet } from '../models/hplc-bench-sheet';
import { AnalysisScopeType, BenchSheetTechnologyType, DocumentType, Entities, NoteType } from '../models/enums';
import NoteViewer from './NoteViewer';
import BenchSheetToolbar from './BenchSheetToolbar';
import HplcDetectorComponent from './HplcDetectorComponent';
import { ItemBasicInformation } from '../models/item-basic-information';
import { DocumentationConstants } from '../store/documentation-constants';
import HplcImpuritiesDataComponent from './HplcImpuritiesDataComponent';
import HplcCoaGradientsData from './HplcCoaGradientsData';
import MinimumSampleSizeComponent from './MinimumSampleSizeComponent';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import AnalysisDataData from './AnalysisDataData';
//import BatchStandardsComponentData from './BatchStandardsComponentData';
import HplcInstrument from './HplcInstrument';
import BenchSheetColumnInformation from './BenchSheetColumnInformation';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetData from './BenchSheetData';
import GradientsData from './GradientsData';
import { FieldUpdate } from '../models/field-update';
import { CrudFunctions } from '../utilities/crud-functions';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import TicketLogEntryForm from './TicketLogEntryForm';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import CheckBox from 'devextreme-react/check-box';

let key = 0;

export default function HplcBenchSheetForm() {
    const [benchSheet, setBenchSheet] =
        useState<HplcBenchSheet>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [issuesFound, setIssuesFound] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;
    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "QCBS-A", "QCBS-L"];
    const canInsert = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "QCBS-A"];
    const canDelete = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    const batchId = benchSheet && benchSheet.batchId ? benchSheet.batchId : undefined;

    //const includesImpurities = benchSheet && benchSheet.analysisTypes ?
    //  benchSheet.analysisTypes.some(a => a.id === BenchSheetAnalysisType.Impurity) : false;

    return (
        <div className="pw-content">
            <BenchSheetToolbar title="HPLC Bench Sheet"
                subtitle="High Performance Liquid Chromatography"
                auditRowPointer={benchSheetId} entity={Entities.HplcBenchSheets}
                technologyId={BenchSheetTechnologyType.HighPerformanceLiquidChromatography}
                documentationId={DocumentationConstants.HPLCBenchSheet}
                showItemInformation={showBenchSheetInformationDialog}
                showTicketLog={showTicketLogEntryDialog} />

            <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

            <HplcInstrument benchSheetId={benchSheetId} isReadOnly={!canEdit}
                onInstrumentChanged={onInstrumentChanged} />

            <HplcDetectorComponent benchSheetId={benchSheetId} isReadOnly={!canEdit}
                instrumentId={benchSheet && benchSheet.instrumentId ? benchSheet.instrumentId : undefined} />

            <div className="pw-margin-bottom-05">
                <div className="lq-sub-title pw-margin-top-1 pw-margin-bottom-05">Diluent and Mobile Phase Preparation</div>
                <SamplePreparationsComponent benchSheetId={benchSheetId}
                    diluent={true} />
            </div>

            <div className="pw-margin-bottom-1">
                <CheckBox id="isocratic" text="Isocratic" value={benchSheet && benchSheet.isocratic ?
                    benchSheet.isocratic : false}
                    onValueChanged={onIsocraticValueChanged} />
            </div>

            {!benchSheet?.isocratic &&
                <GradientsData benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    isIsocratic={benchSheet ? benchSheet.isocratic : false}
                    title="HPLC Gradients" />
            }

            <HplcCoaGradientsData benchSheetId={benchSheetId} isReadOnly={!canEdit} showRatioColumn={benchSheet?.isocratic} />

            <BenchSheetColumnInformation benchSheetId={benchSheetId}
                technology={BenchSheetTechnologyType.HighPerformanceLiquidChromatography} readOnly={!canEdit} />

            {(benchSheet && benchSheet.scopeTypes && benchSheet.scopeTypes.includes(AnalysisScopeType.Homogeneity)) &&
                <MinimumSampleSizeComponent benchSheetId={benchSheetId} />
            }

            <div className="pw-margin-bottom-05">
                <div className="lq-sub-title pw-margin-top-1 pw-margin-bottom-05">Sample Weights</div>
                <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />
            </div>

            <div className="pw-comp-row-scroll-fixed-grid">
                <AnalysisDataData
                    benchSheetId={benchSheetId ? benchSheetId : undefined}
                    batchId={batchId}
                    isReadOnly={!canEdit}
                    datagridHeight={'30em'}
                    title="Matrix Analysis Data" />
            </div>

            {/* REWORK: {includesImpurities &&*/}
            <div className="pw-comp-row-scroll-fixed-grid">
                <HplcImpuritiesDataComponent benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    datagridHeight={'24rem'} />
            </div>
            {/*  }*/}

            <div className="pw-comp-row-scroll-fixed-grid">
                <BenchSheetData parentId={benchSheetId}
                    technology={BenchSheetTechnologyType.HighPerformanceLiquidChromatography}
                    readOnly={!canEdit} canDelete={canDelete}
                    documentType={DocumentType.General}
                    imageHandlerTitle="C of A Images" />
            </div>

            {/*<div className="pw-margin-top-1">*/}
            {/*    <StandardsViewer parentId={benchSheetId} dataGridHeight="10rem"*/}
            {/*        technologyId={BenchSheetTechnologyType.GasChromatography} />*/}
            {/*</div>*/}

            <div className="pw-comp-row-scroll-fixed-grid">
                <NoteViewer height='100%' title="Bench Sheet Notes" readOnly={!canEdit}
                    noteType={NoteType.BenchSheet} parentId={benchSheetId} />
            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="HPLC Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onInstrumentChanged(instrumentId?: string) {
        setBenchSheet({ ...benchSheet, instrumentId: instrumentId });
    }


    function onIsocraticValueChanged(e: any) {
        if (!dataLoaded)
            return;

        console.log("onValueChanged");
        console.log(e);

        const caller = e.element.id;
        const currentValue = benchSheet[caller];

        console.log(currentValue);

        if (currentValue === e.value)
            return;
        // else if (!currentValue && !e.value)
        //   return;
        else {
            setBenchSheet({ ...benchSheet, [caller]: e.value });

            let fieldUpdate: FieldUpdate = {
                entity: "HplcBenchSheets",
                field: caller,
                value: e.value,
                rowId: benchSheetId
            };

            updateField(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Value");
                });
        }
    }

    // get functions

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    //put functions

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/hplcbenchsheetfield", fieldUpdate);
    }


    // show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    async function getAllData() {
        await getBenchSheet()
            .then(() => setDataLoaded(true));
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
